import { useMemo } from "react";
import {
  Sparkles,
  TrendingUp,
  AlertTriangle,
  Lightbulb,
  ShieldAlert,
  Target,
  Cpu,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { computeInsights, type Insight, type InsightTip, type InsightTon } from "@/lib/insights";
import { useFilterVersion } from "@/data/filters";
import { cn } from "@/lib/utils";

const TIP_ICON: Record<InsightTip, LucideIcon> = {
  Trend: TrendingUp,
  Anomalie: AlertTriangle,
  Oportunitate: Lightbulb,
  Risc: ShieldAlert,
  Focus: Target,
};

const TON_STYLE: Record<InsightTon, { card: string; icon: string; chip: string }> = {
  pozitiv: {
    card: "border-emerald-200 bg-emerald-50/50",
    icon: "bg-emerald-100 text-emerald-700",
    chip: "bg-emerald-100 text-emerald-700",
  },
  atentie: {
    card: "border-amber-200 bg-amber-50/50",
    icon: "bg-amber-100 text-amber-700",
    chip: "bg-amber-100 text-amber-700",
  },
  negativ: {
    card: "border-rose-200 bg-rose-50/50",
    icon: "bg-rose-100 text-rose-700",
    chip: "bg-rose-100 text-rose-700",
  },
  neutru: {
    card: "border-slate-200 bg-slate-50/60",
    icon: "bg-slate-100 text-slate-600",
    chip: "bg-slate-100 text-slate-600",
  },
};

function InsightCard({ insight }: { insight: Insight }) {
  const Icon = TIP_ICON[insight.tip];
  const style = TON_STYLE[insight.ton];
  return (
    <div className={cn("flex flex-col rounded-xl border p-4", style.card)}>
      <div className="flex items-start gap-3">
        <div className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg", style.icon)}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={cn("rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide", style.chip)}>
              {insight.tip}
            </span>
          </div>
          <h3 className="mt-1.5 text-sm font-semibold leading-snug text-slate-900">{insight.titlu}</h3>
          <p className="mt-1 text-xs leading-relaxed text-slate-600">{insight.text}</p>
        </div>
      </div>
    </div>
  );
}

export function InsightsPanel() {
  const filterVersion = useFilterVersion();
  const insights = useMemo(() => computeInsights(), [filterVersion]);

  return (
    <section>
      <SectionHeader
        id="insighturi"
        icon={Sparkles}
        titlu="Insighturi automate"
        descriere="Concluzii deterministe generate din date (reguli clare, fără AI) — se recalculează după filtrele active."
      />

      <div className="mb-4 flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-500">
        <Cpu className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
        <span>
          Aceste observații sunt <strong>generate automat</strong> din reguli pe date (trend, deviație vs.
          medie, marjă minimă, capacitate liberă, concentrare client, rebut, livrări). O{" "}
          <strong>analiză AI narativă mai bogată</strong> (recomandări în limbaj natural) se activează la
          conectarea cheii AI.
        </span>
      </div>

      {insights.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-400">
          Fără suficiente date pe filtrul curent pentru a genera insighturi.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {insights.map((i) => (
            <InsightCard key={i.id} insight={i} />
          ))}
        </div>
      )}
    </section>
  );
}
